import { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppAuth } from '../hooks/useAppAuth';
import { useRole } from '../hooks/useRole';
import { createApiClient, platformFeeConfigService, identityVerificationService, walletService } from '../services/api';
import {
  IdentityVerificationType, FEE_ROLE_LABELS, FEE_CHANNEL_LABELS,
  type PlatformFeeRule, type UpsertPlatformFeeRuleInput, WalletTopUpMethod,
} from '../types';

const DOC_TYPE_LABELS: Record<IdentityVerificationType, string> = {
  [IdentityVerificationType.DrivingLicence]: 'Driving Licence',
  [IdentityVerificationType.UtilityBill]: 'Utility Bill',
  [IdentityVerificationType.Passport]: 'Passport',
};

const FEE_CODES = [
  'BAZAAR_SALE', 'CLASSIFIED_LISTING', 'DELIVERY_COMMISSION', 'REPAIR_COMMISSION',
  'BAZAAR_DEPOSIT_MIN', 'WALLET_TOPUP', 'SECURITY_DEPOSIT', 'CASHBACK', 'REFERRAL_BONUS',
  'LATE_CANCELLATION', 'NO_SHOW',
] as const;

const BASIS_CODES = ['GROSS', 'ONLINE_RECEIVED', 'COD_PORTION', 'NONE'] as const;
const CHANNEL_CODES = ['', 'STRIPE_CONNECT', 'COD', 'INTERNAL', 'BANK_TRANSFER'] as const;
const ROLE_IDS = ['', '0', '1', '2'] as const;

const emptyRule: UpsertPlatformFeeRuleInput = {
  id: 0, feeCode: 'BAZAAR_SALE', roleId: null, channelCode: null, basisCode: 'GROSS',
  direction: 'CHARGE', rate: 0, flatAmount: 0, minFee: null, maxFee: null,
  effectiveFrom: new Date().toISOString(), effectiveTo: null, description: '', isActive: true,
};

/** A rate of 0.02 reads as 2% — rules store proportions, admins think in percent. */
const asPercent = (rate: number) => `${(rate * 100).toFixed(2).replace(/\.?0+$/, '')}%`;

export default function AdminPlatformFeesPage() {
  const { getToken } = useAppAuth();
  const { role, isAdminOrOps, isRoleLoading } = useRole();
  const qc = useQueryClient();
  const isStrictAdmin = role === 'admin';

  const { data: rules = [], isLoading } = useQuery({
    queryKey: ['platform-fee-rules'],
    queryFn: () => platformFeeConfigService(createApiClient(getToken)).getRules(),
    enabled: isAdminOrOps,
  });

  const [editing, setEditing] = useState<UpsertPlatformFeeRuleInput | null>(null);

  const saveMutation = useMutation({
    mutationFn: (rule: UpsertPlatformFeeRuleInput) => {
      if (rule.rate < 0 || rule.rate > 1) throw new Error('Rate must be a proportion between 0 and 1 (0.02 = 2%).');
      if (rule.flatAmount < 0) throw new Error('Flat amount cannot be negative.');
      return platformFeeConfigService(createApiClient(getToken)).upsertRule(rule);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['platform-fee-rules'] });
      setEditing(null);
    },
    onError: (e: any) => alert(e?.response?.data?.error ?? e.message ?? 'Failed to save.'),
  });

  // Rules read best grouped by what they charge for, with the wildcard row first so the
  // fallback each role falls back to is obvious.
  const grouped = useMemo(() => {
    const map = new Map<string, PlatformFeeRule[]>();
    for (const r of rules) {
      if (!map.has(r.feeCode)) map.set(r.feeCode, []);
      map.get(r.feeCode)!.push(r);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [rules]);

  if (isRoleLoading) return <div className="text-center py-10 text-gray-400">Loading…</div>;
  if (!isAdminOrOps) return <div className="text-center py-10 text-gray-500">Access restricted to Admin and Ops users.</div>;

  return (
    <div className="page-shell-tight">
      <h1 className="text-xl font-bold text-gray-900 mb-1">Platform Fees</h1>
      <p className="text-sm text-gray-500 mb-6">
        Every platform charge, credit and policy setting lives here as a rule. A rule matches on
        fee&nbsp;&times;&nbsp;role&nbsp;&times;&nbsp;channel — “any” is the fallback, and the most
        specific match wins. Changing a rate or switching a fee off takes effect immediately, with
        no deployment.
      </p>

      {!isStrictAdmin && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-4">
          Only Admin users (not Ops) can change fee rules.
        </p>
      )}

      {isLoading ? (
        <div className="text-center py-10 text-gray-400">Loading…</div>
      ) : (
        <>
          <div className="flex justify-end mb-3">
            <button
              onClick={() => setEditing({ ...emptyRule })}
              disabled={!isStrictAdmin}
              data-testid="add-fee-rule"
              className="text-sm font-semibold px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-50"
            >
              Add rule
            </button>
          </div>

          {grouped.map(([feeCode, feeRules]) => (
            <div key={feeCode} className="mb-6" data-testid={`fee-group-${feeCode}`}>
              <h2 className="text-sm font-semibold text-gray-700 mb-2">{feeCode.replace(/_/g, ' ')}</h2>
              <div className="overflow-x-auto border border-gray-200 rounded-xl bg-white">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Role</th>
                      <th className="text-left px-3 py-2 font-medium">Channel</th>
                      <th className="text-left px-3 py-2 font-medium">Applies to</th>
                      <th className="text-right px-3 py-2 font-medium">Rate</th>
                      <th className="text-right px-3 py-2 font-medium">Flat</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                      <th className="px-3 py-2" />
                    </tr>
                  </thead>
                  <tbody>
                    {feeRules.map(r => (
                      <tr
                        key={r.id}
                        data-testid={`fee-rule-${r.feeCode}-${r.roleId ?? 'any'}-${r.channelCode ?? 'any'}`}
                        className={`border-t border-gray-100 ${r.isActive ? '' : 'opacity-50'}`}
                      >
                        <td className="px-3 py-2">{FEE_ROLE_LABELS[String(r.roleId ?? '')]}</td>
                        <td className="px-3 py-2">{FEE_CHANNEL_LABELS[r.channelCode ?? '']}</td>
                        <td className="px-3 py-2 text-gray-500">{r.basisCode.replace(/_/g, ' ').toLowerCase()}</td>
                        <td className="px-3 py-2 text-right font-medium" data-testid="rule-rate">{asPercent(r.rate)}</td>
                        <td className="px-3 py-2 text-right font-medium" data-testid="rule-flat">£{r.flatAmount.toFixed(2)}</td>
                        <td className="px-3 py-2">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded ${
                            r.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-600'}`}>
                            {r.isActive ? 'Active' : 'Disabled'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-right">
                          <button
                            onClick={() => setEditing({ ...r })}
                            disabled={!isStrictAdmin}
                            data-testid={`edit-rule-${r.id}`}
                            className="text-xs font-semibold text-blue-600 disabled:opacity-40"
                          >
                            Edit
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </>
      )}

      {editing && (
        <RuleEditor
          rule={editing}
          onChange={setEditing}
          onCancel={() => setEditing(null)}
          onSave={() => saveMutation.mutate(editing)}
          saving={saveMutation.isPending}
        />
      )}

      <WalletTopUpQueue />
      <IdentityVerificationQueue />
    </div>
  );
}

function RuleEditor({
  rule, onChange, onCancel, onSave, saving,
}: {
  rule: UpsertPlatformFeeRuleInput;
  onChange: (r: UpsertPlatformFeeRuleInput) => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
}) {
  const set = (patch: Partial<UpsertPlatformFeeRuleInput>) => onChange({ ...rule, ...patch });

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-xl p-5 w-full max-w-lg max-h-[90vh] overflow-y-auto" data-testid="rule-editor">
        <h3 className="text-base font-bold text-gray-900 mb-4">
          {rule.id === 0 ? 'New fee rule' : `Edit rule #${rule.id}`}
        </h3>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Fee">
            <select value={rule.feeCode} onChange={e => set({ feeCode: e.target.value })}
              data-testid="rule-fee-code" className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2">
              {FEE_CODES.map(c => <option key={c} value={c}>{c.replace(/_/g, ' ')}</option>)}
            </select>
          </Field>

          <Field label="Role">
            <select value={rule.roleId === null ? '' : String(rule.roleId)}
              onChange={e => set({ roleId: e.target.value === '' ? null : Number(e.target.value) })}
              data-testid="rule-role" className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2">
              {ROLE_IDS.map(r => <option key={r} value={r}>{FEE_ROLE_LABELS[r]}</option>)}
            </select>
          </Field>

          <Field label="Channel">
            <select value={rule.channelCode ?? ''}
              onChange={e => set({ channelCode: e.target.value === '' ? null : e.target.value })}
              data-testid="rule-channel" className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2">
              {CHANNEL_CODES.map(c => <option key={c} value={c}>{FEE_CHANNEL_LABELS[c]}</option>)}
            </select>
          </Field>

          <Field label="Applies to">
            <select value={rule.basisCode} onChange={e => set({ basisCode: e.target.value })}
              data-testid="rule-basis" className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2">
              {BASIS_CODES.map(b => <option key={b} value={b}>{b.replace(/_/g, ' ').toLowerCase()}</option>)}
            </select>
          </Field>

          <Field label="Rate (0.02 = 2%)">
            <input type="number" step="0.0001" value={rule.rate}
              onChange={e => set({ rate: Number(e.target.value) })}
              data-testid="rule-rate-input" className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
          </Field>

          <Field label="Flat amount (£)">
            <input type="number" step="0.01" value={rule.flatAmount}
              onChange={e => set({ flatAmount: Number(e.target.value) })}
              data-testid="rule-flat-input" className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
          </Field>
        </div>

        <Field label="Description">
          <input value={rule.description ?? ''} onChange={e => set({ description: e.target.value })}
            data-testid="rule-description" className="w-full text-sm border border-gray-300 rounded-lg px-3 py-2" />
        </Field>

        <label className="flex items-center gap-2 mt-3 text-sm text-gray-700">
          <input type="checkbox" checked={rule.isActive} data-testid="rule-active"
            onChange={e => set({ isActive: e.target.checked })} />
          Active — switching this off disables the fee without deleting the rule
        </label>

        <div className="flex gap-2 mt-5">
          <button onClick={onSave} disabled={saving} data-testid="save-rule"
            className="text-sm font-semibold px-4 py-2 rounded-lg bg-blue-600 text-white disabled:opacity-50">
            {saving ? 'Saving…' : 'Save rule'}
          </button>
          <button onClick={onCancel} className="text-sm font-semibold px-4 py-2 rounded-lg border border-gray-300">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-1">
      <label className="text-xs font-medium text-gray-600 block mb-1">{label}</label>
      {children}
    </div>
  );
}

/**
 * Ops confirm that bank transfers actually landed. Approving credits the wallet and closes
 * the request in one transaction, so a row can never be approved without the money arriving.
 */
function WalletTopUpQueue() {
  const { getToken } = useAppAuth();
  const qc = useQueryClient();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [reason, setReason] = useState('');

  const { data: pending = [], isLoading } = useQuery({
    queryKey: ['wallet-topups-pending'],
    queryFn: () => walletService(createApiClient(getToken)).getPendingTopUps(),
  });

  const approve = useMutation({
    mutationFn: (id: string) => walletService(createApiClient(getToken)).approveTopUp(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['wallet-topups-pending'] }),
    onError: (e: any) => alert(e?.response?.data?.error ?? 'Failed to approve.'),
  });

  const reject = useMutation({
    mutationFn: ({ id, r }: { id: string; r: string }) =>
      walletService(createApiClient(getToken)).rejectTopUp(id, r),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wallet-topups-pending'] });
      setRejectingId(null);
      setReason('');
    },
    onError: (e: any) => alert(e?.response?.data?.error ?? 'Failed to reject.'),
  });

  return (
    <div className="border-t border-gray-200 mt-10 pt-6" data-testid="wallet-topup-queue">
      <h2 className="text-lg font-bold text-gray-900 mb-1">Wallet Bank Transfers ({pending.length})</h2>
      <p className="text-sm text-gray-500 mb-3">
        Bank transfers credit nothing until confirmed here. Card top-ups settle through Stripe and never appear in this queue.
      </p>

      {isLoading ? (
        <p className="text-gray-400 text-sm">Loading…</p>
      ) : pending.length === 0 ? (
        <p className="text-gray-400 text-sm" data-testid="no-pending-topups">No bank transfers awaiting confirmation.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {pending.map(t => (
            <div key={t.id} data-testid={`pending-topup-${t.id}`} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
              <div className="flex items-center justify-between mb-1">
                <p className="font-semibold text-gray-900 text-sm">{t.userName}</p>
                <span className="text-base font-bold text-gray-900" data-testid="topup-amount">£{t.amount.toFixed(2)}</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                {t.userEmail} · Reference <span className="font-mono" data-testid="topup-reference">{t.bankReference}</span>
                {' · '}Requested {new Date(t.createdAt).toLocaleDateString('en-GB')}
              </p>
              <p className="text-xs text-gray-500 mb-3">
                Fee £{t.feeAmount.toFixed(2)} · Charged £{t.chargedAmount.toFixed(2)}
                {t.method === WalletTopUpMethod.BankTransfer && ' (bank transfers are free)'}
              </p>

              {rejectingId === t.id ? (
                <div className="flex flex-col gap-2">
                  <input value={reason} onChange={e => setReason(e.target.value)}
                    placeholder="Why is this being rejected?" data-testid="reject-reason"
                    className="text-sm border border-gray-300 rounded-lg px-3 py-2" />
                  <div className="flex gap-2">
                    <button onClick={() => reject.mutate({ id: t.id, r: reason })}
                      disabled={!reason.trim() || reject.isPending} data-testid="confirm-reject"
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-600 text-white disabled:opacity-50">
                      Confirm Reject
                    </button>
                    <button onClick={() => { setRejectingId(null); setReason(''); }}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-300">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => approve.mutate(t.id)} disabled={approve.isPending}
                    data-testid={`approve-topup-${t.id}`}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-green-600 text-white disabled:opacity-50">
                    Confirm funds received
                  </button>
                  <button onClick={() => setRejectingId(t.id)} data-testid={`reject-topup-${t.id}`}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-red-300 text-red-600">
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function IdentityVerificationQueue() {
  const { getToken } = useAppAuth();
  const qc = useQueryClient();
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectNotes, setRejectNotes] = useState('');

  const { data: pending = [], isLoading } = useQuery({
    queryKey: ['identity-verifications-pending'],
    queryFn: () => identityVerificationService(createApiClient(getToken)).getPending(),
  });

  const approveMutation = useMutation({
    mutationFn: (id: string) => identityVerificationService(createApiClient(getToken)).approve(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['identity-verifications-pending'] }),
    onError: (e: any) => alert(e.message ?? 'Failed to approve.'),
  });

  const rejectMutation = useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string }) =>
      identityVerificationService(createApiClient(getToken)).reject(id, notes),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['identity-verifications-pending'] });
      setRejectingId(null);
      setRejectNotes('');
    },
    onError: (e: any) => alert(e.message ?? 'Failed to reject.'),
  });

  return (
    <div className="border-t border-gray-200 mt-10 pt-6">
      <h2 className="text-lg font-bold text-gray-900 mb-3">Bazaar Identity Verification Queue ({pending.length})</h2>
      {isLoading ? (
        <p className="text-gray-400 text-sm">Loading…</p>
      ) : pending.length === 0 ? (
        <p className="text-gray-400 text-sm">No pending identity verifications.</p>
      ) : (
        <div className="flex flex-col gap-3">
          {pending.map(v => (
            <div key={v.id} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
              <div className="flex items-center justify-between mb-1">
                <p className="font-semibold text-gray-900 text-sm">{v.userName}</p>
                <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-800">Pending</span>
              </div>
              <p className="text-xs text-gray-500 mb-2">
                {DOC_TYPE_LABELS[v.verificationType]} · Submitted {new Date(v.submittedAt).toLocaleDateString('en-GB')}
              </p>
              <a href={v.documentUrl} target="_blank" rel="noreferrer" className="text-xs text-blue-600 underline">View document</a>

              {rejectingId === v.id ? (
                <div className="flex flex-col gap-2 mt-3">
                  <input value={rejectNotes} onChange={(e) => setRejectNotes(e.target.value)}
                    placeholder="Reason for rejection" className="text-sm border border-gray-300 rounded-lg px-3 py-2" />
                  <div className="flex gap-2">
                    <button onClick={() => rejectMutation.mutate({ id: v.id, notes: rejectNotes })}
                      disabled={!rejectNotes.trim() || rejectMutation.isPending}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-red-600 text-white disabled:opacity-50">
                      Confirm Reject
                    </button>
                    <button onClick={() => { setRejectingId(null); setRejectNotes(''); }}
                      className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-gray-300">
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 mt-3">
                  <button onClick={() => approveMutation.mutate(v.id)} disabled={approveMutation.isPending}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-green-600 text-white disabled:opacity-50">
                    Approve
                  </button>
                  <button onClick={() => setRejectingId(v.id)}
                    className="text-xs font-semibold px-3 py-1.5 rounded-lg border border-red-300 text-red-600">
                    Reject
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}